import { Eye, Edit2, Trash2, Check } from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import Pagination from "../../../common/Pagination.jsx";
import { SearchBar, EmptyState } from "../../index.jsx";
import { screenService } from "../../../../services/admin/screenService";
import ConfirmDeleteModal from "../users/ConfirmDeleteModal.jsx";

export default function ScreenTable({ onEdit }) {
  const [screens, setScreens] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 1500);
  };
  
  const loadScreens = async () => {
    try {
      const response = await screenService.getAll();
      setScreens(response.data || []);
    } catch (error) {
      console.error("Error loading screens:", error);
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const response = await screenService.getAll();
        setScreens(response.data || []);
      } catch (error) {
        console.error("Error loading screens:", error);
      }
    })();
  }, []);
  const handleSearchChange = (val) => { setSearch(val); setCurrentPage(1); };
  const handleItemsPerPageChange = (val) => { setItemsPerPage(val); setCurrentPage(1); };

  const handleDeleteConfirm = async () => {
    try {
      await screenService.delete({
        screenId: selectedScreen.screenId,
        modifiedby: localStorage.getItem("userId"),
      });
      setShowDeleteModal(false);
      loadScreens();
      showToast("Screen deleted successfully", "success");
    } catch (error) {
      console.error("Delete error:", error);
      setShowDeleteModal(false);
      showToast("Failed to delete screen. Please try again.", "error");
    }
  };

  const filtered = useMemo(
    () => screens.filter((s) => s.screenName?.toLowerCase().includes(search.toLowerCase())),
    [screens, search]
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = filtered.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
      <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-auto max-h-[calc(100vh-140px)]">

      {/* Header */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">
          Screen Directory
        </h3>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
          <SearchBar
            value={search}
            onChange={handleSearchChange}
            placeholder="Search screens..."
          />
        </div>
      </div>

      {/* Table */}
      <div className="w-full flex-1 overflow-auto hide-scrollbar">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-slate-50/50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Sr.No</th>
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Parent Screen</th>
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Screen Name</th>
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Screen URL</th>
              <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {currentRecords.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState query={search} label="screens" />
                </td>
              </tr>
            ) : (
              currentRecords.map((s, index) => (
                <tr key={s.screenId} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group">
                  <td className="px-6 py-3 text-slate-700 dark:text-slate-300">{startIndex + index + 1}</td>
                  <td className="px-6 py-3 text-slate-600 dark:text-slate-400">{s.parentScreen}</td>
                  <td className="px-6 py-3 font-semibold text-slate-800 dark:text-slate-200">{s.screenName}</td>
                  <td className="px-6 py-3 text-slate-600 dark:text-slate-400">{s.screenURL}</td>
                  <td className="px-6 py-3 text-center">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shadow-sm">
                        <button
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          onClick={() => onEdit(s)}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                          onClick={() => {
                            setSelectedScreen(s);
                            setShowDeleteModal(true);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      </div>

      {selectedScreen && createPortal(
        <ConfirmDeleteModal
          module={'Screen'}
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          userName={selectedScreen.screenName}
        />,
        document.body
      )}

      {toast.visible && createPortal(
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"}`}>
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}>✕</span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>,
        document.body
      )}
    </>
  );
}
